import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getExamAttemptPayloadForStudent, saveExamAnswer, submitExamAttempt } from '../services/exam2Api'
import { useResponsiveContext } from '../context/ResponsiveContext'
import { FillBlankAnswerForm } from '../components/exam/FillBlankAnswerForm'
import { DefinitionAnswerForm } from '../components/exam/DefinitionAnswerForm'
import { ExplanationAnswerForm } from '../components/exam/ExplanationAnswerForm'
import { MatchingAnswerForm } from '../components/exam/MatchingAnswerForm'
import { PassageGroupAnswerForm } from '../components/exam/PassageGroupAnswerForm'
import { McqAnswerForm } from '../components/exam/McqAnswerForm'
import { TrueFalseAnswerForm } from '../components/exam/TrueFalseAnswerForm'
import { FigureQuestionAnswerForm } from '../components/exam/FigureQuestionAnswerForm'

const btn = {
  padding: '8px 12px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#ffffff',
  cursor: 'pointer',
  fontSize: 13,
}

const primaryBtn = {
  ...btn,
  background: '#1e3a8a',
  border: '1px solid #1e3a8a',
  color: '#ffffff',
}

function attemptStatusLabel(status) {
  if (!status) return 'غير معروف'
  switch (status) {
    case 'in_progress':
      return 'قيد التنفيذ'
    case 'submitted':
      return 'تم الإرسال'
    case 'graded':
      return 'تم التصحيح'
    case 'open':
      return 'مفتوح'
    case 'closed':
      return 'مغلق'
    case 'scheduled':
      return 'مقرر'
    default:
      return status
  }
}

export default function ExamAttemptPlayer() {
  const navigate = useNavigate()
  const { sessionId, attemptId } = useParams()
  const { isMobile } = useResponsiveContext()

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const [attempt, setAttempt] = useState(null)
  const [sessionRow, setSessionRow] = useState(null)
  const [paper, setPaper] = useState(null)
  const [questions, setQuestions] = useState([])
  const [answersByQid, setAnswersByQid] = useState({})

  // Timer and autosave helpers
  const [remainingSeconds, setRemainingSeconds] = useState(null)
  const lastSavedRef = useRef({})
  const autosavingRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (!attemptId) return
      setLoading(true)
      setErrorMsg('')
      try {
        const payload = await getExamAttemptPayloadForStudent(attemptId)
        if (cancelled || !payload) return
        const qs = Array.isArray(payload.questions) ? payload.questions : []
        const ans = Array.isArray(payload.answers) ? payload.answers : []
        const byQid = {}
        ans.forEach(a => {
          if (!a) return
          const qid = String(a.question_id)
          byQid[qid] = a
        })
        setAttempt(payload.attempt || null)
        setSessionRow(payload.session || null)
        setPaper(payload.paper || null)
        setQuestions(qs)
        setAnswersByQid(byQid)
        // Initialize lastSaved snapshot for autosave comparison
        lastSavedRef.current = JSON.parse(JSON.stringify(byQid || {}))
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e?.message || 'فشل تحميل بيانات الامتحان')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [attemptId])

  const orderedQuestions = useMemo(() => {
    const arr = Array.isArray(questions) ? [...questions] : []
    arr.sort((a, b) => (a.position || 0) - (b.position || 0))
    return arr
  }, [questions])

  const answerableQuestions = useMemo(
    () => orderedQuestions.filter(q => q.type_key !== 'section'),
    [orderedQuestions]
  )

  const [currentIndex, setCurrentIndex] = useState(0) // index into answerableQuestions

  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= answerableQuestions.length) setCurrentIndex(0)
  }, [answerableQuestions.length, currentIndex])

  function localAnswerValue(q) {
    const key = String(q.id)
    const existing = answersByQid[key]
    if (!existing || existing.answer == null) return null
    return existing.answer
  }

  function updateLocalAnswer(q, newAnswer) {
    const key = String(q.id)
    setAnswersByQid(prev => {
      const prevRow = prev[key] || { question_id: q.id, attempt_id: attempt?.id }
      return {
        ...prev,
        [key]: { ...prevRow, answer: newAnswer },
      }
    })
  }

  async function handleSave(q) {
    if (!attempt || !attempt.id || !q?.id) return
    const key = String(q.id)
    const row = answersByQid[key]
    setSaving(true)
    setErrorMsg('')
    try {
      await saveExamAnswer({ attemptId: attempt.id, questionId: q.id, answer: row?.answer ?? {} })
      // Update snapshot for this question after a successful manual save
      if (row) {
        lastSavedRef.current = {
          ...lastSavedRef.current,
          [key]: JSON.parse(JSON.stringify(row)),
        }
      }
    } catch (e) {
      setErrorMsg(e?.message || 'فشل حفظ الإجابة')
    } finally {
      setSaving(false)
    }
  }

  async function handleSubmit() {
    if (!attempt || !attempt.id) return
    if (!window.confirm('هل أنت متأكد من إرسال الامتحان؟ لن تتمكن من التعديل بعد الإرسال.')) return
    setSubmitting(true)
    setErrorMsg('')
    try {
      const updated = await submitExamAttempt(attempt.id)
      setAttempt(updated)
      // After submission, redirect to the read-only review page for this attempt
      navigate(`/exams/${sessionId}/attempt/${updated.id || attempt.id}/review`)
    } catch (e) {
      setErrorMsg(e?.message || 'فشل إرسال الامتحان')
    } finally {
      setSubmitting(false)
    }
  }

  const currentQuestion = answerableQuestions[currentIndex] || null

  // Helper to compute remaining seconds based on session window and optional duration
  function computeRemainingSeconds(attemptObj, sessionObj) {
    if (!attemptObj || !sessionObj) return null
    if (attemptObj.status !== 'in_progress') return null

    const sessionEnd = sessionObj.ends_at ? new Date(sessionObj.ends_at) : null
    let cutoff = sessionEnd

    if (sessionObj.duration_minutes != null && attemptObj.started_at) {
      const durMs = Number(sessionObj.duration_minutes) * 60 * 1000
      const attemptStart = new Date(attemptObj.started_at)
      if (!Number.isNaN(attemptStart.getTime()) && Number.isFinite(durMs) && durMs > 0) {
        const attemptCutoff = new Date(attemptStart.getTime() + durMs)
        cutoff = sessionEnd
          ? new Date(Math.min(sessionEnd.getTime(), attemptCutoff.getTime()))
          : attemptCutoff
      }
    }

    if (!cutoff || Number.isNaN(cutoff.getTime())) return null

    const now = new Date()
    const diffMs = cutoff.getTime() - now.getTime()
    const secs = Math.floor(diffMs / 1000)
    return secs <= 0 ? 0 : secs
  }

  // Timer effect – updates remainingSeconds every second while attempt is in progress
  useEffect(() => {
    if (!attempt || !sessionRow) {
      setRemainingSeconds(null)
      return
    }

    const initial = computeRemainingSeconds(attempt, sessionRow)
    setRemainingSeconds(initial)

    if (attempt.status !== 'in_progress' || initial == null) return undefined

    const id = setInterval(() => {
      setRemainingSeconds(prev => {
        const next = computeRemainingSeconds(attempt, sessionRow)
        return next
      })
    }, 1000)

    return () => clearInterval(id)
  }, [attempt, sessionRow])

  // Periodic autosave – every 20s save answers that changed since last snapshot
  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress') return undefined

    const id = setInterval(async () => {
      if (autosavingRef.current) return
      if (!attempt || attempt.status !== 'in_progress') return

      autosavingRef.current = true
      try {
        const current = answersByQid || {}
        const prev = lastSavedRef.current || {}
        const entries = Object.entries(current)
        const toSave = []

        entries.forEach(([qid, row]) => {
          const prevRow = prev[qid]
          const currAnswer = row?.answer ?? null
          const prevAnswer = prevRow?.answer ?? null
          if (JSON.stringify(currAnswer) !== JSON.stringify(prevAnswer)) {
            toSave.push({ qid, row })
          }
        })

        if (!toSave.length) return

        // Fire saves sequentially to keep load modest
        for (const item of toSave) {
          if (!item.row?.question_id) continue
          // eslint-disable-next-line no-await-in-loop
          await saveExamAnswer({
            attemptId: attempt.id,
            questionId: item.row.question_id,
            answer: item.row.answer ?? {},
          })
        }

        // Update snapshot after successful autosave
        const nextSnapshot = {}
        Object.entries(current).forEach(([qid, row]) => {
          nextSnapshot[qid] = JSON.parse(JSON.stringify(row))
        })
        lastSavedRef.current = nextSnapshot
      } catch (e) {
        // Keep error message minimal to avoid spamming the user
        setErrorMsg(prev => prev || e?.message || 'تعذر الحفظ التلقائي.')
      } finally {
        autosavingRef.current = false
      }
    }, 20000)

    return () => clearInterval(id)
  }, [attempt, answersByQid])

  function findSectionForQuestion(q) {
    if (!q) return null
    const pos = q.position || 0
    const sectionsBefore = orderedQuestions.filter(
      it => it.type_key === 'section' && (it.position || 0) <= pos
    )
    if (!sectionsBefore.length) return null
    // last section before or at this question
    return sectionsBefore.sort((a, b) => (a.position || 0) - (b.position || 0))[sectionsBefore.length - 1]
  }

  function renderQuestionBody(q) {
    const answer = localAnswerValue(q)
    const type = q.type_key

    if (type === 'section') {
      return (
        <div style={{ fontSize: 14, color: '#4b5563' }}>
          {q.payload?.instructions && (
            <div style={{ marginBottom: 8 }}>{q.payload.instructions}</div>
          )}
          <div style={{ fontSize: 13, color: '#6b7280' }}>هذا قسم تنظيمي ولا يحتاج إلى إجابة.</div>
        </div>
      )
    }

    if (type === 'mcq') {
      return (
        <McqAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'true_false') {
      return (
        <TrueFalseAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'fill_blank') {
      return (
        <FillBlankAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'definition') {
      return (
        <DefinitionAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'explanation') {
      return (
        <ExplanationAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'matching') {
      return (
        <MatchingAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'passage_group') {
      return (
        <PassageGroupAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    if (type === 'figure_question') {
      return (
        <FigureQuestionAnswerForm
          q={q}
          answer={answer}
          onChange={(question, newAnswer) => updateLocalAnswer(question, newAnswer)}
        />
      )
    }

    // Fallback: simple text answer for any other type (including custom)
    const generic = typeof answer?.value === 'string' ? answer.value : ''
    return (
      <textarea
        value={generic}
        onChange={e => updateLocalAnswer(q, { value: e.target.value })}
        style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', minHeight: 120, resize: 'vertical' }}
      />
    )
  }

  if (loading) {
    return (
      <div
        dir="rtl"
        style={{
          minHeight: '100vh',
          backgroundColor: '#f3f4f6',
          padding: '1.5rem',
        }}
      >
        <div style={{ maxWidth: 960, margin: '0 auto' }}>جاري تحميل الامتحان...</div>
      </div>
    )
  }

  if (errorMsg && !attempt) {
    return (
      <div
        dir="rtl"
        style={{
          minHeight: '100vh',
          backgroundColor: '#f3f4f6',
          padding: '1.5rem',
        }}
      >
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ padding: 10, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#7f1d1d' }}>{errorMsg}</div>
        </div>
      </div>
    )
  }

  if (!attempt || !sessionRow || !paper) {
    return null
  }

  const title = paper.title || sessionRow.title || 'امتحان أونلاين'
  const timeIsOver = remainingSeconds === 0

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '1.5rem',
      }}
    >
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
            display: 'grid',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{title}</h1>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                الحالة:
                {' '}
                {attemptStatusLabel(attempt.status)}
              </div>
              {remainingSeconds != null && (
                <div style={{ fontSize: 13, color: remainingSeconds <= 60 ? '#b91c1c' : '#111827', marginTop: 4 }}>
                  الوقت المتبقي:
                  {' '}
                  {Math.floor(remainingSeconds / 60)
                    .toString()
                    .padStart(2, '0')}
                  :
                  {(remainingSeconds % 60).toString().padStart(2, '0')}
                </div>
              )}
              {timeIsOver && (
                <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>
                  انتهى الوقت المحدد لهذا الامتحان. قد لا تُقبل أي تغييرات جديدة على الإجابات.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {saving && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>جاري الحفظ...</div>
              )}
              {autosavingRef.current && !saving && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>جاري الحفظ التلقائي...</div>
              )}
              {attempt.status === 'in_progress' && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={primaryBtn}
                >
                  {submitting ? 'جاري الإرسال...' : 'إرسال الامتحان'}
                </button>
              )}
            </div>
          </div>

          {errorMsg && attempt && (
            <div style={{ padding: 10, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#7f1d1d' }}>{errorMsg}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,3fr) minmax(0,1.2fr)', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ display: 'grid', gap: 10 }}>
              {currentQuestion ? (
                <>
                  {findSectionForQuestion(currentQuestion) && (
                    <div
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        padding: 10,
                        background: '#f9fafb',
                        display: 'grid',
                        gap: 6,
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {findSectionForQuestion(currentQuestion).prompt || 'فقرة تعليمية'}
                      </div>
                      {findSectionForQuestion(currentQuestion).payload?.instructions && (
                        <div style={{ fontSize: 13, color: '#4b5563', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                          {findSectionForQuestion(currentQuestion).payload.instructions}
                        </div>
                      )}
                    </div>
                  )}

                  <div
                    style={{
                      display: 'grid',
                      gap: 10,
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 12,
                      background: '#ffffff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'grid', gap: 2 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          س
                          {currentIndex + 1}
                          .
                          {' '}
                          {currentQuestion.prompt || 'بدون نص'}
                        </div>
                        {currentQuestion.payload?.type_header && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{currentQuestion.payload.type_header}</div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#4b5563', textAlign: 'left' }}>
                        {currentQuestion.points != null && (
                          <span style={{ marginInlineStart: 8 }}>
                            الدرجة:
                            {' '}
                            {currentQuestion.points}
                          </span>
                        )}
                        {currentQuestion.required === false ? (
                          <span style={{ marginInlineStart: 8 }}>سؤال اختياري</span>
                        ) : (
                          <span style={{ marginInlineStart: 8 }}>سؤال إجباري</span>
                        )}
                      </div>
                    </div>

                    <div>{renderQuestionBody(currentQuestion)}</div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleSave(currentQuestion)}
                        style={btn}
                      >
                        {saving ? 'جاري الحفظ...' : 'حفظ الإجابة'}
                      </button>
                      <button
                        type="button"
                        disabled={currentIndex <= 0}
                        onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                        style={btn}
                      >
                        السؤال السابق
                      </button>
                      <button
                        type="button"
                        disabled={currentIndex >= answerableQuestions.length - 1}
                        onClick={() => setCurrentIndex(i => Math.min(answerableQuestions.length - 1, i + 1))}
                        style={btn}
                      >
                        السؤال التالي
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: '#6b7280' }}>لا توجد أسئلة متاحة في هذا الامتحان.</div>
              )}
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#ffffff', fontSize: 13 }}>
                <div style={{ marginBottom: 4 }}>
                  <strong>عدد الأسئلة القابلة للإجابة:</strong>
                  {' '}
                  {answerableQuestions.length}
                </div>
                <div style={{ marginBottom: 4 }}>
                  <strong>السؤال الحالي:</strong>
                  {' '}
                  {answerableQuestions.length ? currentIndex + 1 : '-'}
                  /
                  {answerableQuestions.length || '-'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  <button
                    type="button"
                    onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                    disabled={currentIndex <= 0}
                    style={btn}
                  >
                    السابق
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentIndex(i => Math.min(answerableQuestions.length - 1, i + 1))}
                    disabled={currentIndex >= answerableQuestions.length - 1}
                    style={btn}
                  >
                    التالي
                  </button>
                </div>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#ffffff', fontSize: 13 }}>
                <div style={{ marginBottom: 6 }}><strong>خريطة الأسئلة:</strong></div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {answerableQuestions.map((q, idx) => {
                    const key = String(q.id)
                    const ansRow = answersByQid[key]
                    const hasAnswer = ansRow && ansRow.answer != null
                    const isCurrent = idx === currentIndex
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setCurrentIndex(idx)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 999,
                          border: '1px solid',
                          borderColor: isCurrent ? '#1e3a8a' : hasAnswer ? '#16a34a' : '#e5e7eb',
                          background: isCurrent ? '#1e3a8a' : hasAnswer ? '#dcfce7' : '#ffffff',
                          color: isCurrent ? '#ffffff' : hasAnswer ? '#166534' : '#111827',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        {idx + 1}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#ffffff', display: 'grid', gap: 6, fontSize: 13 }}>
                <div style={{ fontWeight: 700 }}>معلومات الجلسة</div>
                <div>
                  تاريخ البداية:
                  {' '}
                  {sessionRow.starts_at ? new Date(sessionRow.starts_at).toLocaleString('ar-EG') : '—'}
                </div>
                <div>
                  تاريخ النهاية:
                  {' '}
                  {sessionRow.ends_at ? new Date(sessionRow.ends_at).toLocaleString('ar-EG') : '—'}
                </div>
                {sessionRow.duration_minutes != null && (
                  <div>
                    المدة:
                    {' '}
                    {sessionRow.duration_minutes}
                    {' '}
                    دقيقة
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
